import crypto from 'crypto';

export type PiiType =
  | 'phone'
  | 'email'
  | 'first_name'
  | 'last_name'
  | 'date_of_birth'
  | 'gender'
  | 'city'
  | 'state'
  | 'street_address'
  | 'zip_code'
  | 'country'
  | 'external_id';

const onlyLettersRegex = /[^a-z]/gi;
const lettersNumbersRegex = /[^a-z0-9]/gi;
const nonDigitsRegex = /\D+/g;

function sha256(input: string): string {
  return crypto.createHash('sha256').update(input).digest('hex');
}

function normalizeEmail(value: string): string | null {
  const normalized = value?.trim().toLowerCase();
  return normalized || null;
}

function normalizePhone(value: string): string | null {
  if (!value) return null;
  return value.replace(nonDigitsRegex, '');
}

function normalizeDateOfBirth(value: string): string | null {
  if (!value) return null;
  const digits = value.replace(nonDigitsRegex, '');
  if (digits.length === 8) return digits; // expects YYYYMMDD

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const yyyy = String(date.getUTCFullYear()).padStart(4, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

function normalizeGender(value: string): string | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (['f', 'female', 'woman', 'girl', 'w'].includes(v)) return 'f';

  if (['m', 'male', 'man', 'boy'].includes(v)) return 'm';
  return null;
}

function normalizeName(value: string): string | null {
  if (!value) return null;
  const cleaned = value.trim().toLowerCase().replace(lettersNumbersRegex, '');
  return cleaned || null;
}

function normalizeCity(value: string): string | null {
  if (!value) return null;
  const cleaned = value.trim().toLowerCase().replace(onlyLettersRegex, '');
  return cleaned || null;
}

function normalizeTwoLetter(value: string): string | null {
  if (!value) return null;
  const letters = value.trim().toLowerCase().replace(onlyLettersRegex, '');
  if (letters.length >= 2) return letters.slice(0, 2);
  return null;
}

function normalizeStreetAddress(value: string): string | null {
  if (!value) return null;
  const cleaned = value.trim().toLowerCase().replace(lettersNumbersRegex, '');
  return cleaned || null;
}

function normalizeZip(value: string): string | null {
  if (!value) return null;
  const part = value
    .toLowerCase()
    .split('-')[0]
    .trim()
    .replace(lettersNumbersRegex, '');
  return part.length >= 2 ? part : null;
}

function normalizeExternalId(value: string): string | null {
  const normalized = value?.toString().trim();
  return normalized || null;
}

function normalize(value: string, type: PiiType): string | null {
  if (typeof value !== 'string') return null;

  switch (type) {
    case 'email':
      return normalizeEmail(value);
    case 'phone':
      return normalizePhone(value);
    case 'date_of_birth':
      return normalizeDateOfBirth(value);
    case 'gender':
      return normalizeGender(value);
    case 'first_name':
    case 'last_name':
      return normalizeName(value);
    case 'city':
      return normalizeCity(value);
    case 'state':
    case 'country':
      return normalizeTwoLetter(value);
    case 'zip_code':
      return normalizeZip(value);
    case 'external_id':
      return normalizeExternalId(value);
    case 'street_address':
      return normalizeStreetAddress(value);
    default:
      return null;
  }
}

/**
 * Normalize and hash PII per.
 * Returns null when input is empty or cannot be normalized.
 */
export function hashPii(value: string, type: PiiType): string | null {
  const normalized = normalize(value, type);
  if (!normalized) return null;
  return sha256(normalized);
}

/**
 * Raw SHA-256 hash helper (no normalization).
 */
export function hash(input: string): string {
  return sha256(input);
}
